import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getInstitutionDetailSummaryByID } from "Explore/Services/Institution";

const useAdminInstitution = () => {
  const user = useSelector((state) => state.user?.data);
  const adminInstitutionId = user?.admin_institution_id;
  const adminInstitutionName = user?.admin_institution_name;

  const [institution, setInstitution] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const derivedIsLoading = isLoading || (!!adminInstitutionId && !institution && !error);

  useEffect(() => {
    if (!adminInstitutionId) {
      setInstitution(null);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const fetchInstitution = async () => {
      try {
        const data = await Promise.race([
          new Promise((resolve) => {
            getInstitutionDetailSummaryByID(adminInstitutionId, resolve);
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Timeout fetching institution summary")), 15000)
          ),
        ]);

        if (isMounted) {
          if (data && data.name) {
            setInstitution({ ...data, id: adminInstitutionId, type: "institution" });
          } else {
            // Fallback
            setInstitution({
              id: adminInstitutionId,
              name: adminInstitutionName || "Unknown Institution",
              type: "institution",
            });
          }
        }
      } catch (err) {
        console.error("Failed to fetch admin institution summary:", err);
        if (isMounted) {
          setError(err.message || "Failed to load institution details.");
          // Fallback on error
          setInstitution({
            id: adminInstitutionId,
            name: adminInstitutionName || "Unknown Institution",
            type: "institution",
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchInstitution();

    return () => {
      isMounted = false;
    };
  }, [adminInstitutionId, adminInstitutionName]);

  return {
    institution,
    isLoading: derivedIsLoading,
    error,
    isAdmin: Boolean(user?.is_admin || !!adminInstitutionId || user?.role === "admin"),
  };
};

export default useAdminInstitution;
